import api from './api';

export default new class Ajax {

    async addRoleToUser(userId, roleId) {
        return await api.post(`/api/user-has-roles`, {
            role_id: roleId,
            user_id: userId
        })
            .then(res => res.data)
            .catch(res => res)
    }

    async changeActivityObjectInProcess(id, value) {
        return await api.put(`/api/objects-in-process/change-activity/${id}`, {is_active: value});
    }

    async createMaterial(data) {
        return await api.post(`/api/materials`, data);
    }

    async createMaterialGroups(data) {
        return await api.post(`/api/material-groups`, data);
    }

    async createMaterialTargets(data) {
        return await api.post(`/api/material-targets`, data);
    }

    async changeNotificationObjectInProcess(id, value) {
        return await api.put(`/api/objects-in-process/change-notification/${id}`, {need_notification: value});
    }

    changeUserActive(id, payload) {
        return api.put(`/api/users/change-active/${id}`, payload).then(res => res.data);
    }

    getUserStatusImpact(id) {
        return api.get(`/api/users/${id}/status-impact`).then(res => res.data);
    }

    getUserDeletionImpact(id) {
        return api.get(`/api/users/${id}/deletion-impact`).then(res => res.data);
    }

    async createObject(data) {
        return await api.post(`/api/objects`, data);
    }

    async createObjectsInProcess(data) {
        return api.post(`/api/objects-in-process`, data).then(res => res.data);
    }

    async createSystemUser(data) {
        return await api.post(`/api/system-users`, data);
    }

    createUserGroup(payload) {
        return api.post(`/api/user-groups`, payload).then(({data}) => data);
    }

    async createTarget(data) {
        return await api.post(`/api/targets`, data);
    }

    createTelegramGroup(payload) {
        return api.post(`/api/telegram/groups`, payload).then(({data}) => data);
    }

    addUsersToTelegram(id, payload) {
        return api.put(`/api/telegram/groups/${id}`, payload).then(({data}) => data);
    }

    async createWorkType(data) {
        return api.post(`/api/types-of-work`, data);
    }

    async createWorkGroup(data) {
        return api.post(`/api/work-groups`, data).then(res => res.data);
    }

    async createUser(data) {
        return api.post(`/api/users`, data);
    }

    async getEmployeeDirectorySettings() {
        return api.get(`/api/users/directory-settings`).then(res => res.data);
    }

    async updateEmployeeDirectorySettings(payload) {
        return api.put(`/api/users/directory-settings`, payload).then(res => res.data);
    }

    async getEmploymentOrganizations(includeInactive = false) {
        return api.get(`/api/users/employment-organizations`, {
            params: {full: includeInactive}
        }).then(res => res.data);
    }

    async createEmploymentOrganization(payload) {
        return api.post(`/api/users/employment-organizations`, payload).then(res => res.data);
    }

    async previewGoogleContactsImport(file) {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/api/users/imports/google-contacts/preview`, formData, {
            headers: {'Content-Type': 'multipart/form-data'}
        }).then(res => res.data);
    }

    async applyGoogleContactsImport({file, fileSha256, decisions}) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('apply', 'true');
        formData.append('file_sha256', fileSha256 || '');
        formData.append('decisions', JSON.stringify(decisions || {}));
        return api.post(`/api/users/imports/google-contacts/apply`, formData, {
            headers: {'Content-Type': 'multipart/form-data'}
        }).then(res => res.data);
    }

    async exportGoogleContacts(params = {}) {
        return api.get(`/api/users/export/google-contacts.csv`, {
            params,
            responseType: 'blob'
        });
    }

    async exportEmployeeGoogleContact(userId) {
        return api.get(`/api/users/${userId}/export/google-contacts.csv`, {
            responseType: 'blob'
        });
    }

    async getMyProfile() {
        return api.get(`/api/users/profile/me`).then(res => res.data);
    }

    async getNavigationSettings() {
        return api.get(`/api/users/navigation/me`).then(res => res.data);
    }

    async updatePreferredWorkspace(preferredWorkspaceKey) {
        return api.put(`/api/users/navigation/me`, {
            preferred_workspace_key: preferredWorkspaceKey || null
        }).then(res => res.data);
    }

    async getHomeSummary() {
        return api.get(`/api/home`).then(res => res.data);
    }

    async clearHomeShiftReportDrafts(drafts) {
        return api.post('/api/home/shift-report-drafts/clear', {drafts}).then(res => res.data);
    }

    async getTaskAttentionCount() {
        return api.get(`/api/tasks-management/attention-count`).then(res => res.data);
    }

    async updateMyProfile(payload) {
        return api.put(`/api/users/profile/me`, payload).then(res => res.data);
    }

    async getEmployeeCard(userId) {
        return api.get(`/api/users/${userId}/card`).then(res => res.data);
    }
    async getEmployeeContactCard(userId) {
        return api.get(`/api/employees/by-user/${userId}`, {ignoreModal: true}).then(res => res.data);
    }

    async uploadUserAvatar(userId, file) {
        const formData = new FormData();
        formData.append('avatar', file);
        return api.post(`/api/users/${userId}/avatar`, formData, {
            headers: {'Content-Type': 'multipart/form-data'}
        }).then(res => res.data);
    }

    async deleteUserAvatar(userId) {
        return api.delete(`/api/users/${userId}/avatar`).then(res => res.data);
    }

    async deleteMaterial(id) {
        return api.delete(`/api/materials/${id}`);
    }

    async deleteMaterialGroup(id) {
        return api.delete(`/api/material-groups/${id}`);
    }

    async deleteMaterialTarget(id) {
        return api.delete(`/api/material-targets/${id}`);
    }

    async deleteRolesByUser(id) {
        return api.delete(`/api/user-has-roles/user/${id}`);
    }

    async deleteObject(id) {
        return api.delete(`/api/objects/${id}`);
    }

    async deleteObjectInProcess(id) {
        return api.delete(`/api/objects-in-process/${id}`);
    }

    async deleteRolesByUserId(userId) {
        return api.delete(`/api/user-has-roles/user/${userId}`);
    }

    async deleteSystemUser(userId) {
        return await api.delete(`/api/system-users/${userId}`);
    }

    async deleteTarget(data) {
        const {building_process_id, work_id} = data;
        return api.delete(`/api/targets`, {
            params: {
                building_process_id,
                work_id
            }
        })
    }

    async deleteUser(userId, payload = {}) {
        return api.delete(`/api/users/${userId}`, {data: payload});
    }

    deleteUserGroup(id) {
        return api.delete(`/api/user-groups/${id}`);
    }

    async deleteWorkType(workId) {
        return api.delete(`/api/types-of-work/${workId}`);
    }

    async setWorkTypeStatus(workId, isActive) {
        return api.patch(`/api/types-of-work/${workId}/status`, {is_active: Boolean(isActive)});
    }

    async editMaterial(id, data) {
        return api.put(`/api/materials/${id}`, data);
    }

    async setMaterialStatus(id, isActive) {
        return api.patch(`/api/materials/${id}/status`, {is_active: Boolean(isActive)});
    }

    async editMaterialGroup(id, data) {
        return api.put(`/api/material-groups/${id}`, data);
    }

    async editMaterialTarget(id, data) {
        return api.put(`/api/material-targets/${id}`, data);
    }

    async editObject(objectId, data) {
        return api.put(`/api/objects/${objectId}`, data);
    }

    async editObjectInProcess(processId, data) {
        return api.put(`/api/objects-in-process/${processId}`, data);
    }

    async editSystemUser(userId, data) {
        return await api.put(`/api/system-users/${userId}`, data);
    }

    async editTarget(data) {
        return api.put(`/api/targets`, data);
    }

    editUserGroup(id, payload) {
        return api.put(`/api/user-groups/${id}`, payload);
    }

    getUserGroups() {
        return api.get(`/api/user-groups`).then(({data}) => data);
    }

    async editWorkType(workId, data) {
        return api.put(`/api/types-of-work/${workId}`, data);
    }

    async editWorkGroup(id, data) {
        return api.put(`/api/work-groups/${id}`, data);
    }

    async getCompletedWorks() {
        return api.get(`/api/current-works`).then(res => res.data).catch(res => res);
    }

    async getCompletedWorksWithSecondWorks(archive) {
        return api.get(`/api/current-works/with-second-works`, {
            params: {
                archive
            }
        }).then(res => res.data).catch(res => res);
    }

    async createFuelLimit(data) {
        return api.post(`/api/fuel-limits`, data)
            .then(res => res.data);
    }

    async updateFuelLimit(data) {
        return api.put(`/api/fuel-limits`, data)
            .then(res => res.data);
    }

    async deleteFuelLimits(building_process_id, fuel_id, author_id) {
        return api.delete(`/api/fuel-limits/${building_process_id}`, {
            params: {
                fuel_id,
                author_id
            }
        }).then(res => res.data);
    }

    async getFuelLimits(archive) {
        return api.get(`/api/fuel-limits`, {
            params: {
                archive
            }
        }).then(res => res.data).catch(res => res);
    }

    async createFuelCard(data) {
        return api.post(`/api/fuel-cards`, data).then(res => res.data);
    }

    async updateFuelCard(id, data) {
        return api.put(`/api/fuel-cards/${id}`, data).then(res => res.data);
    }

    async archiveFuelCard(id) {
        return api.delete(`/api/fuel-cards/${id}`).then(res => res.data);
    }

    async getFuelCards(archive) {
        return api.get(`/api/fuel-cards`, {
            params: {
                archive
            }
        }).then(res => res.data);
    }

    async getFuelCardHistory(id) {
        return api.get(`/api/fuel-cards/${id}/history`).then(res => res.data);
    }

    async getFuelCardAccessSettings() {
        return api.get(`/api/fuel-cards/access-settings`).then(res => res.data);
    }

    async updateFuelCardAccessSettings(access_config) {
        return api.put(`/api/fuel-cards/access-settings`, {access_config}).then(res => res.data);
    }

    async getMechanicAssetAccessSettings() {
        return api.get(`/api/mechanic-assets/access-settings`).then(res => res.data);
    }

    async updateMechanicAssetAccessSettings(access_config) {
        return api.put(`/api/mechanic-assets/access-settings`, {access_config}).then(res => res.data);
    }

    async getMechanicAssetStatuses() {
        return api.get(`/api/mechanic-assets/statuses`).then(res => res.data);
    }

    async getMechanicAssetLocationTypes(context) {
        return api.get(`/api/mechanic-assets/location-types`, {params: {context}}).then(res => res.data);
    }

    async getMechanicAssetObjectGroups() {
        return api.get(`/api/mechanic-assets/object-groups`).then(res => res.data);
    }

    async getMechanicAssetPositionFieldOptions() {
        return api.get(`/api/mechanic-assets/position-field-options`).then(res => res.data);
    }

    async getMechanicAssetFuelNormTypes() {
        return api.get(`/api/mechanic-assets/fuel-norm-types`).then(res => res.data);
    }

    async getMechanicAssetWarehouses(params) {
        return api.get(`/api/mechanic-assets/warehouses`, {params}).then(res => res.data);
    }

    async createMechanicAssetWarehouse(data) {
        return api.post(`/api/mechanic-assets/warehouses`, data).then(res => res.data);
    }

    async updateMechanicAssetWarehouse(id, data) {
        return api.put(`/api/mechanic-assets/warehouses/${id}`, data).then(res => res.data);
    }

    async archiveMechanicAssetWarehouse(id, data) {
        return api.delete(`/api/mechanic-assets/warehouses/${id}`, {data}).then(res => res.data);
    }

    async getMechanicAssetCatalog(params) {
        return api.get(`/api/mechanic-assets/catalog`, {params}).then(res => res.data);
    }

    async createMechanicAssetCatalog(data) {
        return api.post(`/api/mechanic-assets/catalog`, data).then(res => res.data);
    }

    async updateMechanicAssetCatalog(id, data) {
        return api.put(`/api/mechanic-assets/catalog/${id}`, data).then(res => res.data);
    }

    async archiveMechanicAssetCatalog(id, data) {
        return api.delete(`/api/mechanic-assets/catalog/${id}`, {data}).then(res => res.data);
    }

    async getMechanicAssetUnits(params) {
        return api.get(`/api/mechanic-assets/units`, {params}).then(res => res.data);
    }

    async getMechanicAssetUnit(id) {
        return api.get(`/api/mechanic-assets/units/${id}`).then(res => res.data);
    }

    async getMechanicAssetHistory(id) {
        return api.get(`/api/mechanic-assets/units/${id}/history`).then(res => res.data);
    }

    async getMechanicAssetNotes(id) {
        return api.get(`/api/mechanic-assets/units/${id}/notes`).then(res => res.data);
    }

    async createMechanicAssetUnit(data) {
        return api.post(`/api/mechanic-assets/units`, data).then(res => res.data);
    }

    async updateMechanicAssetUnit(id, data) {
        return api.put(`/api/mechanic-assets/units/${id}`, data).then(res => res.data);
    }

    async archiveMechanicAssetUnit(id, data) {
        return api.delete(`/api/mechanic-assets/units/${id}`, {data}).then(res => res.data);
    }

    async createMechanicAssetNote(id, data) {
        return api.post(`/api/mechanic-assets/units/${id}/notes`, data).then(res => res.data);
    }

    async uploadMechanicAssetFiles(id, data, config) {
        return api.post(`/api/mechanic-assets/units/${id}/files`, data, config || {}).then(res => res.data);
    }

    async uploadMechanicAssetNoteFiles(id, noteId, data, config) {
        return api.post(`/api/mechanic-assets/units/${id}/notes/${noteId}/files`, data, config || {}).then(res => res.data);
    }

    async setMechanicAssetPrimaryPhoto(id, fileId, data) {
        return api.put(`/api/mechanic-assets/units/${id}/files/${fileId}/primary-photo`, data || {}).then(res => res.data);
    }

    async deleteMechanicAssetFile(id, fileId) {
        return api.delete(`/api/mechanic-assets/units/${id}/files/${fileId}`).then(res => res.data);
    }

    async createMechanicAssetFuelNorm(id, data) {
        return api.post(`/api/mechanic-assets/units/${id}/fuel-norms`, data).then(res => res.data);
    }

    async updateMechanicAssetFuelNorm(id, data) {
        return api.put(`/api/mechanic-assets/fuel-norms/${id}`, data).then(res => res.data);
    }

    async deleteMechanicAssetFuelNorm(id, data) {
        return api.delete(`/api/mechanic-assets/fuel-norms/${id}`, {data}).then(res => res.data);
    }

    async createMechanicAssetMeterRecord(id, data) {
        return api.post(`/api/mechanic-assets/units/${id}/meter-history`, data).then(res => res.data);
    }

    async createMechanicAssetRental(id, data) {
        return api.post(`/api/mechanic-assets/units/${id}/rentals`, data).then(res => res.data);
    }

    async returnMechanicAssetRental(id, data) {
        return api.put(`/api/mechanic-assets/rentals/${id}/return`, data).then(res => res.data);
    }

    async getTypesOfFuel() {
        return api.get(`/api/types-of-fuel`).then(res => res.data).catch(res => res);
    }

    async getImages(limit, skip, query_building, options = {}) {
        return api.get(`/api/photos/report`, {
            cancelToken: options.cancelToken,
            localErrorHandling: true,
            ignoreModal: true,
            params: {
                limit,
                skip,
                query_building,
                report_status: options.report_status,
                work_ids: options.work_ids,
                work_group_ids: options.work_group_ids,
                shifts_limit: options.shifts_limit,
                media_limit: options.media_limit
            }
        }).then(res => res.data).catch(res => res);
    }

    async getImagesForBuilding(id, limit, skip, options = {}) {
        return api.get(`/api/photos/process/${id}`, {
            cancelToken: options.cancelToken,
            localErrorHandling: true,
            ignoreModal: true,
            params: {
                limit,
                skip,
                query_building: options.query_building,
                search_scope: options.search_scope,
                report_status: options.report_status,
                work_ids: options.work_ids,
                work_group_ids: options.work_group_ids,
                media_limit: options.media_limit
            }
        }).then(res => res.data).catch(res => res);
    }

    async getGalleryAccessSettings() {
        return api.get(`/api/photos/access-settings`).then(res => res.data);
    }

    async updateGalleryAccessSettings(access_config) {
        return api.put(`/api/photos/access-settings`, {access_config}).then(res => res.data);
    }

    async getGalleryObjectAccess() {
        return api.get(`/api/photos/object-access`).then(res => res.data);
    }

    async updateGalleryObjectAccess(id, access_config) {
        return api.put(`/api/photos/object-access/${id}`, {access_config}).then(res => res.data);
    }

    async getImage(id) {
        return api.get(`/api/photos/${id}`, {responseType: 'blob'}).then(res => res.data).catch(res => res);
    }

    async getMiniImage(id) {
        return api.get(`/api/photos/mini/${id}`, {responseType: 'blob'}).then(res => res.data).catch(res => res);
    }

    async getMe() {
        return await api.get(`/auth/get-me`).then(res => {
            return res.data[0];
        })
    }

    async getRoles() {
        return await api.get(`/api/user-roles`).then(res => {
            return res.data;
        }).catch(res => {
            return res;
        })
    }

    async getSystemUsers() {
        return await api.get(`/api/system-users`).then(res => res.data);
    }

    async getSystemUser(userId) {
        return await api.get(`/api/system-users/${userId}`);
    }

    async getUsedFuels(archive) {
        return api.get(`/api/current-fuel`, {
            params: {
                archive
            }
        }).then(res => res.data);
    }

    async getUsers(full = false) {
        return api.get(`/api/users`, {
            params: {
                full
            }
        }).then(res => res.data);
    }

    async getUser(userId) {
        return api.get(`/api/users/${userId}`).then(res => res.data);
    }

    async getUserHistory(userId) {
        return api.get(`/api/users/${userId}/history`, {ignoreModal: true}).then(res => res.data);
    }

    async getDepartments(all = false, options = {}) {
        return api.get(`/api/departments`, {
            ignoreModal: options.ignoreModal === true,
            params: {
                all: all
            }
        }).then(res => res.data);
    }

    async createDepartment(payload) {
        return api.post(`/api/departments`, payload, {ignoreModal: true}).then(res => res.data);
    }

    async updateDepartment(id, payload) {
        return api.put(`/api/departments/${id}`, payload, {ignoreModal: true}).then(res => res.data);
    }

    async deleteDepartment(id) {
        return api.delete(`/api/departments/${id}`, {ignoreModal: true}).then(res => res.data);
    }

    async getDepartment(id) {
        return api.get(`/api/departments/${id}`, {ignoreModal: true}).then(res => res.data);
    }

    async getObjects() {
        return api.get(`/api/objects`).then(res => {
            return res.data;
        }).catch(res => {
            return res;
        })
    }

    async getObjectsInProcess(archive = false, options = {}) {
        return api.get(`/api/objects-in-process`, {
            ignoreModal: options.silent === true,
            params: {
                archive
            }
        }).then(res => {
            return res.data;
        }).catch(res => {
            return res;
        })
    }

    async getSecondWorks() {
        return api.get(`/api/works-notes`)
            .then(res => res.data)
            .catch(res => res);
    }

    async getTargets(archive) {
        return api.get(`/api/targets`, {
            params: {
                archive
            }
        }).then(res => res.data).catch(res => res);
    }

    async getMaterial(id) {
        return api.get(`/api/materials/${id}`)
            .then(res => res.data)
            .catch(res => res)
    }

    async getMaterials() {
        return api.get(`/api/materials`)
            .then(res => res.data)
            .catch(res => res)
    }

    async getMaterialGroups() {
        return api.get(`/api/material-groups`)
            .then(res => res.data)
            .catch(res => res)
    }

    async getMaterialGroup(id) {
        return api.get(`/api/material-groups/${id}`)
            .then(res => res.data)
            .catch(res => res)
    }

    async getMaterialTargets(archive) {
        return api.get(`/api/material-targets`, {
            params: {
                archive
            }
        })
            .then(res => res.data)
            .catch(res => res)
    }

    async getMaterialTarget(id) {
        return api.get(`/api/material-targets/id/${id}`)
            .then(res => res.data)
            .catch(res => res)
    }

    async getMaterialUsage(archive) {
        return api.get(`/api/current-materials`,
            {
                params: {
                    archive
                }
            })
            .then(res => res.data)
            .catch(res => res)
    }

    async getWorkShifts(archive) {
        return api.get(`/api/works-shifts`, {
            params: {
                withoutDateFormat: true,
                archive
            }
        }).then(res => res.data).catch(res => res);
    }

    async getWorkTypes() {
        return api.get(`/api/types-of-work`).then(res => res.data).catch(res => res);
    }

    async getWorkGroups() {
        return api.get(`/api/work-groups`).then(res => res.data).catch(res => res)
    }

    async login(payload, company) {
        const params = {};
        if (company) {
            params.company = company;
        }
        return api.post(`/auth/login`, payload, {
            params,
            ignoreModal: true
        }).then(({data}) => data);
    }

    checkLoginAvailable(login) {
        return api.post(`/api/system-users/check-available`, {login}).then(({data}) => data);
    }

    setHeaderAuth(token) {
        if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete api.defaults.headers.common['Authorization'];
        }
    }

    async restartNotifications() {
        return api.get(`/api/report-time/restart`);
    }

    async updateUser(userId, user) {
        return api.put(`/api/users/${userId}`, user).then(res => res.data).catch(res => res);
    }

    getAvailableReportsManagement() {
        return api.get(`/api/reports-management/available-reports`).then(({data}) => data);
    }

    getActiveReports() {
        return api.get(`/api/reports-management/active-reports`).then(({data}) => data);
    }

    fillReportsTableByCurrentProcesses(reportId) {
        return api.get(`/api/reports-management/fill-table-by-current-active-processes/report-id/${reportId}`);
    }

    deleteActiveReport(id) {
        return api.delete(`/api/reports-management/active-reports/${id}`).then(({data}) => data);
    }

    deleteActiveReportByProcess(id) {
        return api.delete(`/api/reports-management/active-reports/building-process-id/${id}`).then(({data}) => data);
    }

    runReportsForcibly() {
        return api.get(`/api/reports-management/run-reports-forcibly`).then(({data}) => data)
    }

    createReportManagement(payload) {
        return api.post(`/api/reports-management/active-reports`, payload).then(({data}) => data);
    }

    getAvailableAlertsManagement() {
        return api.get(`/api/alerts-management/available-alerts`).then(({data}) => data);
    }

    getActiveAlerts() {
        return api.get(`/api/alerts-management/active-alerts`).then(({data}) => data);
    }

    fillAlertsTableByCurrentProcesses(alertId) {
        return api.get(`/api/alerts-management/fill-table-by-current-active-processes/alert-id/${alertId}`);
    }

    deleteActiveAlert(id) {
        return api.delete(`/api/alerts-management/active-alerts/${id}`).then(({data}) => data);
    }

    deleteActiveAlertByProcess(id) {
        return api.delete(`/api/alerts-management/active-alerts/building-process-id/${id}`).then(({data}) => data);
    }

    createAlertManagement(payload) {
        return api.post(`/api/reports-management/active-alerts`, payload).then(({data}) => data);
    }

    sendDatePicker(payload) {
        return api.post(`/telegram-datepicker`, payload).then(({data}) => data);
    }

    createTask(payload) {
        return api.post(`/api/tasks-management`, payload, {ignoreModal: true}).then(({data}) => data);
    }

    updateTask(id, payload) {
        return api.put(`/api/tasks-management/${id}`, payload, {ignoreModal: true}).then(({data}) => data);
    }

    getTasks(params = {}) {
        return api.get(`/api/tasks-management`, {params}).then(({data}) => data);
    }

    getTaskById(id) {
        return api.get(`/api/tasks-management/${id}`, {ignoreModal: true}).then(({data}) => data);
    }

    uploadTaskFile(payload, taskId) {
        return api.post(`/api/tasks-management/files`, payload, {
            ignoreModal: true,
            params: {
                taskId
            }
        }).then(({data}) => data);
    }

    addComment(payload) {
        return api.post(`/api/tasks-management/comments`, payload, {ignoreModal: true}).then(({data}) => data);
    }

    getComments(taskId) {
        return api.get(`/api/tasks-management/comments/${taskId}`, {ignoreModal: true}).then(({data}) => data);
    }

    getEventMessages(taskId) {
        return api.get(`/api/tasks-management/event-messages/${taskId}`).then(({data}) => data);
    }

    getLogs(taskId) {
        return api.get(`/api/tasks-management/logs/${taskId}`, {ignoreModal: true}).then(({data}) => data);
    }

    updateComment(id, payload) {
        return api.put(`/api/tasks-management/comments/${id}`, payload, {ignoreModal: true}).then(({data}) => data);
    }

    deleteFile({id, taskId, commentId}) {
        return api.delete(`/api/tasks-management/files/${id}`, {
            ignoreModal: true,
            params: {
                taskId,
                commentId
            }
        }).then(({data}) => data);
    }

    deleteComment({id, task}) {
        return api.delete(`/api/tasks-management/comments/${id}`, {
            ignoreModal: true,
            params: {
                taskId: task.id
            }
        }).then(({data}) => data);
    }

    getShortUsers(options = {}) {
        // This lookup contains only active human accounts. Keep the selector contract locally.
        const params = {...(options.cabinetAccess ? {cabinet_access: true} : {}), ...(options.telegramPolicy ? {telegram_policy: true} : {})};
        return api.get(`/api/users/short`, {params}).then(({data}) => data.map(user => ({...user, active: true, isBot: false})));
    }

}
